(() => {
  const globalKey = 'MarineMaxAIChatRailPlacement';

  if (window[globalKey]) {
    window[globalKey].mount(document);
    return;
  }

  const sectionSelector = '.shopify-section.section-ai-chat-widget';
  const mainContentSelector = '.site-layout-container > .site-main-content';
  const sourceSectionSelector = `#MainContent ${sectionSelector}`;
  const mountedSectionSelector = `.site-layout-container > ${sectionSelector}`;
  const editorRailClass = 'site-layout-container--editor-ai-chat-rail';
  const pendingUnmounts = new WeakSet();

  function getScopedSection(scope) {
    if (!scope) return null;
    if (scope.matches?.(sectionSelector)) return scope;
    return scope.querySelector?.(sectionSelector) || null;
  }

  const isDesignMode = Boolean(
    window.Shopify?.designMode ||
      document.documentElement?.classList?.contains('shopify-design-mode'),
  );

  if (isDesignMode) {
    let syncPending = false;
    let activeEditorWidget = null;

    function syncEditorRail() {
      syncPending = false;
      const layout = document.querySelector('.site-layout-container');
      const section = document.querySelector(sourceSectionSelector);
      const widget = section?.querySelector?.(
        '.ai-chat-widget[data-ai-chat-layout="right_rail"]',
      );
      const app = window.MarineMaxAIChatApp;
      if (
        activeEditorWidget &&
        activeEditorWidget !== widget &&
        typeof app?.destroyScope === 'function'
      ) {
        app.destroyScope(activeEditorWidget);
      }
      activeEditorWidget = widget || null;
      if (widget && typeof app?.initializeScope === 'function') {
        app.initializeScope(widget);
      }
      layout?.classList?.toggle(editorRailClass, Boolean(widget));
      return Boolean(widget);
    }

    function scheduleEditorSync() {
      if (syncPending) return;
      syncPending = true;
      window.setTimeout(syncEditorRail, 0);
    }

    const api = Object.freeze({
      mount: syncEditorRail,
      unmount: scheduleEditorSync,
    });
    Object.defineProperty(window, globalKey, {
      configurable: false,
      enumerable: false,
      writable: false,
      value: api,
    });

    syncEditorRail();
    document.addEventListener('shopify:section:load', scheduleEditorSync);
    document.addEventListener('shopify:section:unload', scheduleEditorSync);
    document.addEventListener('shopify:section:reorder', scheduleEditorSync);

    const layout = document.querySelector('.site-layout-container');
    if (layout && typeof window.MutationObserver === 'function') {
      const observer = new window.MutationObserver(scheduleEditorSync);
      observer.observe(layout, { childList: true, subtree: true });
    }
    return;
  }

  function getCurrentSection() {
    return (
      document.querySelector(sourceSectionSelector) ||
      document.querySelector(mountedSectionSelector)
    );
  }

  function getMountedSections() {
    return Array.from(document.querySelectorAll(mountedSectionSelector));
  }

  function destroySection(section) {
    const app = window.MarineMaxAIChatApp;
    if (app && typeof app.destroyScope === 'function') {
      app.destroyScope(section);
    }
  }

  function detachSection(section) {
    if (typeof section.remove === 'function') {
      section.remove();
      return;
    }
    section.parentNode?.removeChild?.(section);
  }

  function removeSection(section) {
    destroySection(section);
    detachSection(section);
  }

  function removeStaleMountedSections(currentSection) {
    getMountedSections().forEach((section) => {
      if (section !== currentSection) removeSection(section);
    });
  }

  function mount(scope = document) {
    const mainContent = document.querySelector(mainContentSelector);
    const section =
      scope === document ? getCurrentSection() : getScopedSection(scope);

    if (!mainContent || !section) {
      mainContent?.parentNode?.classList?.remove('site-layout-container--ai-chat-rail-pending');
      return false;
    }
    pendingUnmounts.delete(section);
    removeStaleMountedSections(section);
    if (
      section.parentNode === mainContent.parentNode &&
      section.previousElementSibling === mainContent
    ) {
      mainContent.parentNode?.classList?.remove('site-layout-container--ai-chat-rail-pending');
      return true;
    }

    mainContent.insertAdjacentElement('afterend', section);
    mainContent.parentNode?.classList?.remove('site-layout-container--ai-chat-rail-pending');
    return true;
  }

  function unmount(scope) {
    const section = getScopedSection(scope);
    if (!section) return false;

    destroySection(section);
    pendingUnmounts.add(section);
    const cleanup = () => {
      if (!pendingUnmounts.delete(section)) return;
      if (getMountedSections().includes(section)) detachSection(section);
    };

    window.setTimeout(cleanup, 0);
    return true;
  }

  const api = Object.freeze({ mount, unmount });
  Object.defineProperty(window, globalKey, {
    configurable: false,
    enumerable: false,
    writable: false,
    value: api,
  });

  mount(document);
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) =>
    unmount(event.target),
  );
  document.addEventListener('shopify:section:reorder', (event) =>
    mount(event.target),
  );
})();
